import { useState } from "react";
import { toast } from "sonner";
import { Container } from "@/components/Sections";
import NewsletterForm from "@/components/NewsletterForm";
import { submitContact } from "@/lib/api";
import { ArrowRight } from "lucide-react";

const INTERESTS = [
  "Ranger Workforce Development",
  "Carbon Project Readiness",
  "Nature Repair Readiness",
  "Environmental Monitoring Systems",
  "Training Pathways",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organisation: "",
    role: "",
    interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required");
      return;
    }
    setLoading(true);
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        organisation: form.organisation || undefined,
        role: form.role || undefined,
        interest: form.interest || undefined,
        message: form.message,
      });
      toast.success("Message received. We'll be in touch soon.");
      setForm({ name: "", email: "", organisation: "", role: "", interest: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact" className="bg-[#F6F1E6]">
      <section className="pt-40 md:pt-52 pb-16">
        <Container>
          <p className="overline mb-6">Connect</p>
          <h1
            data-testid="contact-title"
            className="display-xxl text-5xl md:text-7xl lg:text-[112px] mb-10"
          >
            Let&apos;s build
            <br />
            <span className="italic text-[#3F5132]">environmental capability.</span>
          </h1>
          <p className="text-[#4F5A48] text-lg md:text-xl max-w-3xl leading-relaxed">
            Tell us about your program, your Country and the work you want to do.
            We respond to every message.
          </p>
        </Container>
      </section>

      <section className="border-t border-[#1F2A1B]/10 py-20 md:py-28">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
            {/* Contact form */}
            <div className="lg:col-span-7">
              <p className="overline mb-6">Contact form</p>
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="space-y-8"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <input
                    name="name"
                    type="text"
                    required
                    placeholder="Your name *"
                    value={form.name}
                    onChange={onChange}
                    className="editorial-input"
                    data-testid="contact-name"
                  />
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="Email *"
                    value={form.email}
                    onChange={onChange}
                    className="editorial-input"
                    data-testid="contact-email"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <input
                    name="organisation"
                    type="text"
                    placeholder="Organisation"
                    value={form.organisation}
                    onChange={onChange}
                    className="editorial-input"
                    data-testid="contact-organisation"
                  />
                  <input
                    name="role"
                    type="text"
                    placeholder="Your role"
                    value={form.role}
                    onChange={onChange}
                    className="editorial-input"
                    data-testid="contact-role"
                  />
                </div>

                <div>
                  <p className="overline mb-5">What&apos;s the focus?</p>
                  <div className="flex flex-wrap gap-2">
                    {INTERESTS.map((i) => {
                      const active = form.interest === i;
                      return (
                        <button
                          type="button"
                          key={i}
                          data-testid={`contact-interest-${i.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() =>
                            setForm((f) => ({ ...f, interest: active ? "" : i }))
                          }
                          className={`text-xs tracking-[0.22em] uppercase px-4 py-3 transition-colors ${
                            active
                              ? "bg-[#C45A3C] text-[#1F2A1B] border border-[#C45A3C]"
                              : "border border-[#1F2A1B]/30 text-[#4F5A48] hover:text-[#1F2A1B] hover:border-[#C45A3C]"
                          }`}
                        >
                          {i}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <textarea
                  name="message"
                  required
                  placeholder="Tell us about your program, your Country and the work you want to do. *"
                  value={form.message}
                  onChange={onChange}
                  className="editorial-input editorial-textarea"
                  data-testid="contact-message"
                  rows={5}
                />

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="btn-primary disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send message"}
                  <ArrowRight size={16} />
                </button>
              </form>
            </div>

            {/* Right column */}
            <aside className="lg:col-span-5 lg:border-l lg:border-[#1F2A1B]/10 lg:pl-16">
              <p className="overline mb-6">Stay informed</p>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-6 text-[#1F2A1B]">
                Subscribe to <span className="italic text-[#3F5132]">field notes.</span>
              </h2>
              <NewsletterForm testidPrefix="contact-newsletter" />

              <div className="mt-16 pt-10 border-t border-[#1F2A1B]/10">
                <p className="overline mb-5">We work with</p>
                <ul className="space-y-4 text-[#1F2A1B] font-serif text-2xl">
                  <li>Indigenous ranger programs</li>
                  <li>Traditional Owner organisations</li>
                  <li>Aboriginal corporations</li>
                  <li>Workforce development partners</li>
                </ul>
              </div>

              <p className="font-serif italic text-2xl text-[#C45A3C] mt-12 leading-snug">
                Country needs people who can measure change.
              </p>
            </aside>
          </div>
        </Container>
      </section>
    </div>
  );
}
